import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IoLockClosedOutline, IoEyeOff, IoEye } from "react-icons/io5";
import { toast } from "react-toastify";
import { useUser } from "../context/UserContext";
import api from "../utils/api";

function ChangePassword() {
  const [token, setToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useUser();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");

    if (tokenParam) {
      setToken(tokenParam);
    } else {
      api
        .get("/api/current-user", { withCredentials: true })
        .then(() => {
          setToken(null);
        })
        .catch(() => {
          toast.error("You must be logged in to change your password.");
          navigate("/login");
        });
    }
  }, [location.search, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.warning("Please fill in all fields.", {
        position: "bottom-right",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.warning("Passwords do not match.", {
        position: "bottom-right",
      });
      return;
    }

    try {
      setLoading(true);

      if (token !== null) {
        await api.post("/reset-password", {
          token,
          newPassword,
        });
      } else {
        await api.post(
          "/change-password",
          { newPassword },
          { withCredentials: true }
        );
      }

      toast.success("Password changed successfully.", {
        position: "bottom-right",
        autoClose: 2000,
      });
      setCurrentUser(null);
      navigate("/login");
    } catch (error) {
      if (error.response?.data) {
        toast.error(error.response.data, {
          position: "bottom-right",
        });
      } else {
        toast.error("Something went wrong. Please try again.", {
          position: "bottom-right",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      {/* LEFT SECTION - Enhanced */}
      <div className="sm:w-1/2 w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col justify-center items-center relative overflow-hidden sm:grid hidden">
        {/* Animated gradient overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:100px_100px]" />
        
        <div className="z-10 text-center px-8 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <motion.div
              className="absolute inset-0 bg-white/10 blur-2xl rounded-full"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <img
              src="/companylogo.png"
              alt="Shri Padmavathi"
              className="h-20 w-auto mb-8 mx-auto bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-2xl relative z-10"
            />
          </motion.div>

          <motion.h1
            className="text-6xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Secure Access
          </motion.h1>

          <motion.p
            className="text-lg text-slate-300 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Create a strong password to protect your account
          </motion.p>
        </div>

        {/* Floating shapes */}
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 border border-white/10 rounded-full"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-24 h-24 border border-white/10 rounded-lg"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      {/* RIGHT SECTION - Enhanced Form */}
      <div className="sm:w-1/2 w-full flex justify-center items-center px-6 py-12 relative z-10">
        <motion.form
          onSubmit={handleSubmit}
          className="bg-white/70 backdrop-blur-2xl p-10 sm:p-12 rounded-3xl shadow-2xl w-full max-w-md border border-white/20 relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative">
            <motion.h2
              className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent mb-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Change Password
            </motion.h2>
            <motion.p
              className="text-slate-500 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Enter your new password below
            </motion.p>

            <div className="space-y-6">
              {/* New Password Input - Floating Label */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="relative">
                  <IoLockClosedOutline
                    className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl transition-all duration-300 ${
                      newPasswordFocused || newPassword
                        ? "text-blue-500"
                        : "text-slate-400"
                    }`}
                  />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onFocus={() => setNewPasswordFocused(true)}
                    onBlur={() => setNewPasswordFocused(false)}
                    className="w-full border-2 border-slate-200 pl-12 pr-12 py-4 rounded-full focus:outline-none focus:border-blue-500 transition-all duration-300 bg-white/80 hover:bg-white"
                    placeholder=" "
                  />
                  <label
                    className={`absolute left-12 transition-all duration-300 pointer-events-none ${
                      newPasswordFocused || newPassword
                        ? "-top-2.5 text-xs text-blue-500 bg-white px-2 left-10"
                        : "top-1/2 -translate-y-1/2 text-slate-400"
                    }`}
                  >
                    New Password
                  </label>
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                  >
                    {showNewPassword ? <IoEyeOff /> : <IoEye />}
                  </button>
                </div>
              </motion.div>

              {/* Confirm Password Input - Floating Label */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="relative">
                  <IoLockClosedOutline
                    className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl transition-all duration-300 ${
                      confirmPasswordFocused || confirmPassword
                        ? "text-blue-500"
                        : "text-slate-400"
                    }`}
                  />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmPasswordFocused(true)}
                    onBlur={() => setConfirmPasswordFocused(false)}
                    className="w-full border-2 border-slate-200 pl-12 pr-12 py-4 rounded-full focus:outline-none focus:border-blue-500 transition-all duration-300 bg-white/80 hover:bg-white"
                    placeholder=" "
                  />
                  <label
                    className={`absolute left-12 transition-all duration-300 pointer-events-none ${
                      confirmPasswordFocused || confirmPassword
                        ? "-top-2.5 text-xs text-blue-500 bg-white px-2 left-10"
                        : "top-1/2 -translate-y-1/2 text-slate-400"
                    }`}
                  >
                    Confirm Password
                  </label>
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <IoEyeOff /> : <IoEye />}
                  </button>
                </div>
              </motion.div>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full mt-8 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 hover:from-slate-900 hover:via-slate-800 hover:to-slate-900 text-white py-4 rounded-full transition-all duration-300 transform hover:shadow-xl hover:shadow-slate-500/50 relative overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              whileHover={!loading ? { scale: 1.02 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <span className="relative z-10 font-semibold text-lg flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  "Reset Password"
                )}
              </span>
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}

export default ChangePassword;